import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AlertApiService } from 'src/app/shared/services/customer-api/alert-api.service';
import { ScoreUtilsService } from 'src/app/shared/utils/score-utils.service';

interface ScoreAlertTrigger {
  score_type: string;
  additional_params?: Record<string, unknown>;
}

export interface ScoreAlert {
  uid: string;
  name: string;
  active: boolean;
  schedule: string;
  channel: string;
  layout: string;
  additional_recipients: string[];
  score_alert_metadata: {
    portfolio_id: string | null;
    entities: string[];
    triggers: ScoreAlertTrigger[];
  };
}

/**
 * Score alerts keep their own copy of a custom score's weights, so an edit or
 * delete in Score Lab has to be pushed to the alerts that use the score.
 */
@Injectable({
  providedIn: 'root',
})
export class CustomScoreAlertsService {
  constructor(
    private alertApi: AlertApiService,
    private scoreUtils: ScoreUtilsService,
  ) {}

  linkedAlerts(scoreUid: string): Observable<ScoreAlert[]> {
    return this.alertApi
      .getAlerts('score')
      .pipe(
        map((result) =>
          ((result?.hits ?? []) as ScoreAlert[]).filter((alert) =>
            alert.score_alert_metadata?.triggers?.some(
              (trigger) => trigger.score_type === scoreUid,
            ),
          ),
        ),
      );
  }

  /** Re-saves the linked alerts whose weights differ from the score; emits how many. */
  syncLinkedAlerts(scoreUid: string): Observable<number> {
    const scoreParams =
      this.scoreUtils.getCustomScoreAlertParams(scoreUid) ?? {};
    return this.linkedAlerts(scoreUid).pipe(
      map((alerts) =>
        alerts
          .map((alert) => withScoreParams(alert, scoreUid, scoreParams))
          .filter((alert) => !!alert),
      ),
      switchMap((stale) => this.saveAll(stale)),
    );
  }

  deactivate(alerts: ScoreAlert[]): Observable<number> {
    return this.saveAll(alerts.map((alert) => ({ ...alert, active: false })));
  }

  private saveAll(alerts: ScoreAlert[]): Observable<number> {
    if (!alerts.length) {
      return of(0);
    }
    return forkJoin(
      alerts.map((alert) => this.alertApi.addAlert(toPayload(alert))),
    ).pipe(map(() => alerts.length));
  }
}

const isScoreParam = (key: string): boolean =>
  key.endsWith('_weight') || key === 'with_black_swan';

const sameParams = (
  a: Record<string, unknown>,
  b: Record<string, unknown>,
): boolean =>
  Object.keys(a).length === Object.keys(b).length &&
  Object.keys(b).every((key) => a[key] === b[key]);

/** The alert with the score's params swapped in, or `null` when nothing changed. */
function withScoreParams(
  alert: ScoreAlert,
  scoreUid: string,
  scoreParams: Record<string, string>,
): ScoreAlert | null {
  let changed = false;
  const triggers = alert.score_alert_metadata.triggers.map((trigger) => {
    if (trigger.score_type !== scoreUid) {
      return trigger;
    }
    const current = trigger.additional_params ?? {};
    const next = {
      ...Object.fromEntries(
        Object.entries(current).filter(([key]) => !isScoreParam(key)),
      ),
      ...scoreParams,
    };
    changed = changed || !sameParams(current, next);
    return { ...trigger, additional_params: next };
  });
  return changed
    ? {
        ...alert,
        score_alert_metadata: { ...alert.score_alert_metadata, triggers },
      }
    : null;
}

// The fields the alert dialog posts; the GET also returns read-only ones.
const toPayload = (alert: ScoreAlert) => ({
  uid: alert.uid,
  active: alert.active,
  schedule: alert.schedule,
  channel: alert.channel,
  layout: alert.layout,
  name: alert.name,
  additional_recipients: alert.additional_recipients,
  score_alert_metadata: alert.score_alert_metadata,
});
